import React, { useContext } from 'react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useFormikContext, Form, Formik } from 'formik';
import { noTrailerCoverageStates, TRAILER_PRIMARY_USE_PLEASURE } from '@ourbranch/lookups';
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';

import { useStore } from 'core/store';
import { AuthContext } from 'core/components/auth';
import { FormField } from 'core/components/form';
import { awsDateFormatter } from 'core/helpers/formatters';
import { Tooltip } from 'core/components/tooltip';
import useOptions from 'core/hooks/use-options';
import useStyles from 'common/components/people/add-form.styles';
import { useDisabledState } from 'common/disabled-context';
import { getDefaultGarageLocation } from 'common/helpers/garage-location-helpers';

const initialValues = {
  trailerVIN: '',
  initialOpen: true
};

const handleSubmit =
  (onAdd, { cars, trailers }) =>
  ({ trailerVIN, initialOpen, type }) => {
    onAdd({
      VIN: trailerVIN.toUpperCase(),
      initialOpen,
      type,
      // Default new trailers to Pleasure use; staff selects Business/Residence/Rental on the
      // trailer card if applicable, which flags the policy for UW review (issue #24533).
      primaryUse: TRAILER_PRIMARY_USE_PLEASURE,
      purchaseDate: awsDateFormatter(new Date()),
      // Default from the first vehicle's garage address, falling back to the first trailer's (#35838).
      garageLocation: getDefaultGarageLocation({ cars, trailers })
    });
  };

const validationSchema = (trailers) =>
  Yup.object().shape({
    trailerVIN: Yup.string()
      .required('VIN is required')
      .ensure()
      .test(
        'is-new-vin',
        'This VIN is already included in your offer',
        (val) => !trailers?.find((trailer) => trailer.VIN === val)
      )
  });

function AddTrailer({ onAdd, id }) {
  const { viewOnly } = useContext(AuthContext);
  const { disabled } = useDisabledState();
  const { values } = useFormikContext();
  const { [id]: trailers, cars } = values || {};

  const classes = useStyles();
  const store = useStore();

  const state = values?.correctedAddress?.state || store?.account?.policies?.policy?.geographicState;
  const noTrailerCoverageInThisState = noTrailerCoverageStates?.includes(state);
  // Brand-aware type list: CFM offers only Recreational/Utility (#35836).
  const trailerTypeOptions = useOptions('trailerType', state);

  return (
    <Formik
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={validationSchema(trailers)}
      onSubmit={handleSubmit(onAdd, { cars, trailers })}
      disabled
    >
      {() => (
        <Form className={classes.container}>
          <Grid key="container" container justifyContent="space-between" alignItems="center" spacing={2}>
            <FormField
              id="trailerVIN"
              name="trailerVIN"
              type="string"
              label={`Trailer's VIN Number`}
              mode="light"
              disabled={noTrailerCoverageInThisState}
              xs={4}
            />
            <FormField
              id="type"
              name="type"
              type="select"
              label="Type"
              mode="light"
              xs={4}
              disabled={noTrailerCoverageInThisState}
              options={trailerTypeOptions}
            />
            {noTrailerCoverageInThisState && (
              <Tooltip
                text={'We are not offering trailer coverage in this state'}
                placement="top"
                className={classes.trailerTip}
              />
            )}
            <Button
              type="submit"
              key="addButton"
              mode="big"
              variant="contained"
              color="secondary"
              className={classes.button}
              disabled={disabled || noTrailerCoverageInThisState || viewOnly}
            >
              Add Trailer
            </Button>
          </Grid>
        </Form>
      )}
    </Formik>
  );
}

AddTrailer.propTypes = {
  onAdd: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired
};

export default AddTrailer;
